import { useDispatch, useSelector } from 'react-redux';

import * as React from 'react';
import { resetLastRooms, resetRoomType, setDealType, setLastRooms, setLastRoomTypes } from '../../actions/filters';
import { setActiveSpecialTab } from '../../actions/specialTabs';
import { FiltersTabs, IFiltersTabProps } from '../../components/FiltersTabs';
import { DEAL_TYPE_NAMES } from '../../constants/dealType';
import { ESpecialTabs } from '../../constants/specialTabs';
import { selectSpecialTabActive } from '../../hooks/selectors/specialTabs';
import { EPageKind } from '../../types/page';
import { dealTypeFromJsonQuery, FDealType } from '../../utils/category';
import { isAddformTabEnabled } from '../../utils/isAddformTabEnabled';
import { trackDealTypeTabClick, trackSpecialTabClick } from './tracking';
import { selectIsBuildersProjectsTabEnabled } from '../../selectors/buildersProjects';
import { selectIsUserAgent } from '../../selectors/user';
import { selectCpdAvailable } from '../../selectors/cpd';
import { setCpdModalOpen } from '../../actions/cpd/actions';
import { trackOpenPopup } from '../CPDModal/tracking';
import { selectJsonQuery } from '../../selectors/filters/selectJsonQuery';
import { selectPageKind } from '../../selectors/pageKind';
import { usePlacementTabProps } from './usePlacementTabProps';

export const FiltersTabsContainer: React.FC = () => {
  const dispatch = useDispatch();

  const placementTabProps = usePlacementTabProps();
  const jsonQuery = useSelector(selectJsonQuery);
  const specialTabActive = useSelector(selectSpecialTabActive);
  const pageKind = useSelector(selectPageKind);
  const isBuildersProjectsTabEnabled = useSelector(selectIsBuildersProjectsTabEnabled);
  const isAgent = useSelector(selectIsUserAgent);
  const isCpdAvailable = useSelector(selectCpdAvailable);

  const currentTabActive = specialTabActive || dealTypeFromJsonQuery(jsonQuery);
  const addformTabEnabled = isAddformTabEnabled(currentTabActive);

  const onDealTypeSelect = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, dealType: FDealType, pageKind: EPageKind) => {
      event.preventDefault();
      if ([FDealType.RentLongterm, FDealType.Sale].includes(dealType)) {
        // подставим фильтры, сохраненные перед переходом на посутку
        dispatch(setLastRoomTypes());
        // очистим фильтры, сохраненные перед переходом на посутку
        dispatch(resetLastRooms());
      } else {
        // при переходе на посутку сохраним фильтры
        dispatch(setLastRooms());
        // очистим текущие фильтры комнатности
        dispatch(resetRoomType());
      }

      dispatch(setDealType(dealType));
      dispatch(setActiveSpecialTab(null));
      trackDealTypeTabClick(pageKind, dealType);
    },
    [dispatch],
  );

  const onSpecialTabSelect = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, tab: ESpecialTabs, pageKind: EPageKind) => {
      event.preventDefault();

      dispatch(setActiveSpecialTab(tab));

      trackSpecialTabClick(pageKind, tab);
    },
    [dispatch],
  );

  const onLinkTabClick = React.useCallback((pageKind: EPageKind, tab: ESpecialTabs) => {
    trackSpecialTabClick(pageKind, tab);
  }, []);

  const onCpdTabClick = React.useCallback(
    ({ pageKind, isCpdAvailable, isAgent }: { pageKind: EPageKind; isCpdAvailable?: boolean; isAgent?: boolean }) => {
      trackSpecialTabClick(pageKind, ESpecialTabs.SearchRealtors);

      if (isCpdAvailable && !isAgent) {
        trackOpenPopup();
        dispatch(setCpdModalOpen(true));
      } else {
        window.open('/podbor-rieltora/', '_self');
      }
    },
    [dispatch],
  );

  const items = React.useMemo(
    () =>
      [
        {
          label: DEAL_TYPE_NAMES[FDealType.Sale],
          url: '/kupit/',
          isSelected: currentTabActive === FDealType.Sale,
          clickHandler: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            onDealTypeSelect(event, FDealType.Sale, pageKind);
          },
        },
        {
          label: DEAL_TYPE_NAMES[FDealType.RentLongterm],
          url: '/snyat/',
          isSelected: currentTabActive === FDealType.RentLongterm,
          clickHandler: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            onDealTypeSelect(event, FDealType.RentLongterm, pageKind);
          },
        },
        {
          label: DEAL_TYPE_NAMES[FDealType.RentDaily],
          url: '/posutochno/',
          isSelected: currentTabActive === FDealType.RentDaily,
          clickHandler: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            onDealTypeSelect(event, FDealType.RentDaily, pageKind);
          },
        },
        isBuildersProjectsTabEnabled && {
          label: 'Построить',
          url: '/country-builders-projects/?utm_medium=main_page&utm_content=bookmark',
          isSelected: false,
          clickHandler: () => {},
        },
        {
          label: 'Оценить',
          url: isAgent ? '/kalkulator-nedvizhimosti/' : '/my-home/?from=mainPage',
          isSelected: false,
          clickHandler: () => {
            onLinkTabClick(pageKind, ESpecialTabs.Valuation);
          },
        },
        {
          label: 'Ипотека',
          url: '/mortgage/',
          isSelected: currentTabActive === ESpecialTabs.Mortgage,
          clickHandler: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            onSpecialTabSelect(event, ESpecialTabs.Mortgage, pageKind);
          },
        },
        {
          label: 'Подбор риелтора',
          isSelected: false,
          clickHandler: () => onCpdTabClick({ pageKind, isCpdAvailable, isAgent }),
        },
        addformTabEnabled && {
          label: placementTabProps?.text || 'Разместить объявление',
          url:
            placementTabProps?.url ||
            ` https://cian.ru/razmestit-obyavlenie/?channel=${
              currentTabActive === FDealType.Sale ? 'from_buy_submission_form' : 'from_rent_submission_form'
            }`,
          isSelected: false,
          clickHandler: () => {},
          datatestId: 'add-offer-tab',
        },
      ].filter(Boolean) as IFiltersTabProps[],
    [
      addformTabEnabled,
      currentTabActive,
      isAgent,
      isBuildersProjectsTabEnabled,
      isCpdAvailable,
      onCpdTabClick,
      onDealTypeSelect,
      onLinkTabClick,
      onSpecialTabSelect,
      pageKind,
      placementTabProps?.text,
      placementTabProps?.url,
    ],
  );

  return <FiltersTabs items={items} />;
};
